import { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';
import { MortgageData } from '@/types/calculator';

/**
 * Transform EnhancedUnifiedFormData to MortgageData for existing components
 */
export const transformToMortgageData = (formData: EnhancedUnifiedFormData): MortgageData => {
  return {
    homePrice: formData.propertyLoan.homePrice,
    downPayment: formData.propertyLoan.downPaymentAmount,
    downPaymentPercentage: formData.propertyLoan.downPaymentPercentage,
    interestRate: formData.propertyLoan.interestRate,
    loanTerm: formData.propertyLoan.loanTerm,
    loanType: formData.propertyLoan.loanType,
    zipCode: formData.personalDetails.zipCode,
    state: formData.personalDetails.state,
    city: formData.personalDetails.county, // Using county as city
    propertyTax: formData.propertyLoan.propertyTaxRate * formData.propertyLoan.homePrice / 100 / 12,
    pidRate: 0, // Default value
    includePID: false, // Default value
    homeInsurance: formData.propertyLoan.homeInsurance,
    pmi: 0, // Will be calculated
    hoaFees: formData.propertyLoan.hoaFees,
    isVeteran: formData.propertyLoan.loanType === 'va',
    creditScore: formData.personalDetails.creditScore,
    propertyType: formData.personalDetails.propertyType === 'multiFamily' ? 'multifamily' : formData.personalDetails.propertyType,
    location: {
      state: formData.personalDetails.state,
      city: formData.personalDetails.county,
    },
    sellerCredit: 0, // Default value
    vaDisabilityStatus: null, // Default value
    vaDisabilityRatingPercentage: null, // Default value
    veteranAge: null, // Default value
    isFirstTimeHomebuyer: formData.personalDetails.isFirstTimeBuyer,
    vaStatus: null, // Default value
    homebuyingHistory: formData.personalDetails.isFirstTimeBuyer ? 'firstTime' : 'other',
    rollFundingFeeIntoLoan: false, // Default value
    earnestMoneyDeposit: 3000,
    monthlyPayment: {
      principal: 0,
      interest: 0,
      taxes: formData.propertyLoan.propertyTaxRate * formData.propertyLoan.homePrice / 100 / 12,
      insurance: formData.propertyLoan.homeInsurance / 12,
      pmi: 0,
      hoa: formData.propertyLoan.hoaFees,
      total: 0,
    },
    closingCosts: {
      total: 0,
      loanOrigination: 0,
      loanOriginationWaived: false,
      appraisalFee: 0,
      titleServices: 0,
      governmentRecordingCharges: 0,
      prepaids: 0,
      initialEscrow: 0,
      other: 0,
      fundingFee: 0,
      sellerCreditAmount: 0,
      totalCashNeeded: 0,
      appraisal: 0,
      inspection: 0,
      titleInsurance: 0,
      lenderFees: 0,
      otherFees: 0,
      sellerCredit: 0,
    },
  };
};

/**
 * Calculate annual income from EnhancedUnifiedFormData
 */
export const calculateAnnualIncome = (formData: EnhancedUnifiedFormData): number => {
  let annualIncome = 0;
  
  // Primary income
  if (formData.primaryIncome.incomeType === 'salary') {
    annualIncome += formData.primaryIncome.annualIncome;
  }
  
  // Additional income
  annualIncome += formData.primaryIncome.additionalIncome;
  
  // Co-borrower income if enabled
  if (formData.coBorrowerIncome.enabled) {
    annualIncome += formData.coBorrowerIncome.additionalIncome;
  }
  
  return annualIncome;
};

/**
 * Extract debt information for analysis
 */
export const extractDebtInformation = (formData: EnhancedUnifiedFormData) => {
  return {
    totalMonthlyDebt: 
      formData.debts.autoLoanPayment +
      formData.debts.creditCardPayment +
      (formData.debts.studentLoanBalance * 0.005) + // 0.5% of balance
      formData.debts.personalLoanPayment +
      formData.debts.childSupportPayment +
      formData.debts.otherDebts,
    breakdown: {
      autoLoan: formData.debts.autoLoanPayment,
      creditCard: formData.debts.creditCardPayment,
      studentLoan: formData.debts.studentLoanBalance * 0.005,
      personalLoan: formData.debts.personalLoanPayment,
      childSupport: formData.debts.childSupportPayment,
      other: formData.debts.otherDebts,
    }
  };
};

/**
 * Transform MortgageData back to EnhancedUnifiedFormData for Supreme Intelligence features
 * This allows the Estimator Engine to use the new Supreme Intelligence components
 */
export const transformFromMortgageData = (mortgageData: MortgageData): EnhancedUnifiedFormData => {
  return {
    personalDetails: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      state: mortgageData.state || 'CA',
      county: mortgageData.city || '',
      zipCode: mortgageData.zipCode || '',
      creditScore: mortgageData.creditScore || 720,
      householdSize: 3,
      propertyType: mortgageData.propertyType === 'multifamily' ? 'multiFamily' : mortgageData.propertyType || 'singleFamily',
      isFirstTimeBuyer: mortgageData.isFirstTimeHomebuyer || false,
    },
    primaryIncome: {
      incomeType: 'salary',
      annualIncome: 75000,
      additionalIncome: 0,
    },
    coBorrowerIncome: {
      enabled: false,
      additionalIncome: 0,
    },
    debts: {
      autoLoanPayment: 0,
      creditCardPayment: 0,
      studentLoanBalance: 0,
      personalLoanPayment: 0,
      childSupportPayment: 0,
      otherDebts: 0,
    },
    propertyLoan: {
      homePrice: mortgageData.homePrice || 400000,
      downPaymentPercentage: mortgageData.downPaymentPercentage || 20,
      downPaymentAmount: mortgageData.downPayment || 80000,
      interestRate: mortgageData.interestRate || 6.5,
      loanTerm: mortgageData.loanTerm || 30,
      propertyTaxRate: mortgageData.propertyTax ? (mortgageData.propertyTax * 12 / mortgageData.homePrice * 100) : 2,
      homeInsurance: mortgageData.homeInsurance * 12 || 1200,
      hoaFees: mortgageData.hoaFees || 0,
      loanType: mortgageData.loanType || 'conventional',
    },
  };
};